// Third Party
import axios, {
  AxiosResponse,
  AxiosInstance,
  InternalAxiosRequestConfig,
  AxiosError,
} from "axios";
import { getLocalStorage } from "../../helpers/utils";

const BASE_APP_URL_v1 = process.env.REACT_APP_BASE_URL_v1;
const createApiInstance = (baseUrl: string): AxiosInstance => {
  const instance = axios.create({
    baseURL: baseUrl,
    timeout: 30000,
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    responseType: "json",
  });

  // Request interceptor for token handling
  instance.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      try {
        config.headers = config.headers || {};
        if (!config.headers.Authorization) {
          const token = getLocalStorage("token");
          config.headers.Authorization = token ? `Bearer ${token}` : "";
        }

        return config;
      } catch (error) {
        console.error("Error in request interceptor:", error);
        return config;
      }
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // An expired session must not leave the app looking signed in. Every panel
  // used to discover it separately and fail on its own - Token Consumption
  // showing an error, Guardrails reporting "couldn't load" - while the page
  // carried on rendering as though nothing had happened, so the user was
  // neither told nor signed out.
  //
  // Only 401. A 403 means signed in but not allowed, which is a real answer to
  // show, not a reason to throw someone back to the login page.
  instance.interceptors.response.use(
    (response: AxiosResponse) => response,
    (error: AxiosError) => {
      const expired = error.response?.status === 401;
      // Pages a signed-out person is meant to be on. A 401 there is that
      // page's own answer (the invite link was refused), not a lapsed session,
      // and the redirect below threw the person to /login before the page
      // could say so: an expired invite looked like a successful sign-up.
      const publicPaths = ["/login", "/admin/setup-account"];
      const alreadyThere = publicPaths.some((path) =>
        window.location.pathname.startsWith(path)
      );
      if (expired && !alreadyThere) {
        try {
          localStorage.removeItem("token");
        } catch {
          // Storage blocked by the browser: the redirect still has to happen.
        }
        window.location.href = "/login";
      }
      return Promise.reject(error);
    }
  );

  return instance;
};

export default createApiInstance;

export const {
  get,
  post,
  put,
  patch,
  delete: destroy,
} = createApiInstance(BASE_APP_URL_v1 as string);
